import type { ContentSummary, Method, Phase, Process } from '@augment-toolkit/core';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { authClient } from '../auth/authClient';
import { config } from '../config';

/**
 * The content service, as RTK Query endpoints. **One API slice for the whole
 * client**, and **no tags** — the API is read-only, so freshness is a
 * `keepUnusedDataFor` question rather than an invalidation one.
 */

/**
 * The service's success envelope. Every 200 from `@augment-toolkit/service-kit`
 * is `{ data: T }`, and every failure is `{ error: { code, message } }`.
 */
type SuccessEnvelope<T> = { data: T };

export type ServiceError = { code: string; message: string };

const rawBaseQuery = fetchBaseQuery({
  baseUrl: config.apiBaseUrl,

  /**
   * Fetched per request rather than held anywhere: `getAccessToken` returns null
   * once the token has expired rather than one the gateway will reject, so a
   * stale token cannot be cached here.
   */
  prepareHeaders: async (headers) => {
    const token = await authClient.getAccessToken();

    if (token !== null) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  },
});

/**
 * `rawBaseQuery` plus two things every endpoint would otherwise repeat.
 *
 * **It refuses to send a request with no session**, because the guard is already
 * taking the user back to login and a request certain to 401 only adds a
 * confusing line to the access log.
 *
 * **It unwraps the `{ data }` envelope once, here** rather than in five
 * `transformResponse`s — the same unwrap written five times and forgotten on the
 * sixth. A body that is not an envelope is a parsing error rather than
 * `undefined`, which is how a contract change reaches the UI as a blank page.
 */
const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const token = await authClient.getAccessToken();

  if (token === null) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: 'Your session has expired. Sign in again to continue.',
      },
    };
  }

  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error !== undefined) {
    return result;
  }

  const body = result.data;

  if (typeof body !== 'object' || body === null || !('data' in body)) {
    return {
      error: {
        status: 'PARSING_ERROR',
        originalStatus: 200,
        data: JSON.stringify(body),
        error: 'The content service returned a body without a data envelope.',
      },
    };
  }

  return { ...result, data: (body as SuccessEnvelope<unknown>).data };
};

/**
 * A `{phase}/{process}/{method}` path, safe to put in a URL. **Per segment, not
 * on the whole string** — `encodeURIComponent` escapes `/`, and the service's
 * greedy `{path+}` variable needs the separators intact.
 */
const encodePath = (path: string): string =>
  path.split('/').map(encodeURIComponent).join('/');

export type GetPhaseResult = { phase: Phase; processes: ContentSummary[] };
export type GetProcessResult = { process: Process; methods: ContentSummary[] };

export const contentApi = createApi({
  reducerPath: 'contentApi',
  baseQuery,

  /**
   * **Five minutes, against a table a scheduler rewrites once a night.** This is
   * how long an entry survives after its last subscriber unmounts, so it is the
   * cost of navigating away and back rather than staleness in any real sense.
   */
  keepUnusedDataFor: 300,

  endpoints: (builder) => ({
    /**
     * The eleven phases, sorted by number — what the L1 grid and the rail both
     * render. **The rail uses this, not the catalog**: 6KB against 260KB.
     */
    listPhases: builder.query<ContentSummary[], void>({
      query: () => '/content/phases',
      transformResponse: (response: { phases: ContentSummary[] }) => response.phases,
    }),

    /** One phase and cards for its processes, in the order they are authored. */
    getPhase: builder.query<GetPhaseResult, string>({
      query: (slug) => `/content/phases/${encodeURIComponent(slug)}`,
    }),

    /** One process and cards for its methods. Path is `phase/process`. */
    getProcess: builder.query<GetProcessResult, string>({
      query: (path) => `/content/processes/${encodePath(path)}`,
    }),

    /** One method. The leaf, so there are no children to fetch. */
    getMethod: builder.query<Method, string>({
      query: (path) => `/content/methods/${encodePath(path)}`,
      transformResponse: (response: { method: Method }) => response.method,
    }),

    /**
     * Every record in the table as a summary — around 500 items, 260KB, **kept
     * for an hour**: longer than a working session and shorter than the
     * scheduler's cycle. See `docs/adr/0002-client-side-search.md`.
     */
    getCatalog: builder.query<ContentSummary[], void>({
      query: () => '/content/catalog',
      transformResponse: (response: { items: ContentSummary[] }) => response.items,
      keepUnusedDataFor: 3600,
    }),
  }),
});

export const {
  useListPhasesQuery,
  useGetPhaseQuery,
  useGetProcessQuery,
  useGetMethodQuery,
  useGetCatalogQuery,
} = contentApi;
